import type { Breadcrumb, ErrorEvent } from "@sentry/react";

export const REDACTED = "[redacted]";

/**
 * Query parameters that carry a credential: the one-time `token` of the verify-email, confirm-email,
 * change-password and set-password links, and anything else named like a secret. The same rule as
 * the API's `isCredentialKey` (log-error-serializer.ts), which the web cannot import.
 */
const CREDENTIAL_PARAM_RE =
	/^(auth|code|pass|api[-_]?key|apikey)$|(password|secret|token|cookie)$/i;

/** A URL with the value of every credential query parameter redacted; anything else as it came. */
export function scrubUrl(url: string | undefined): string | undefined {
	if (!url || !url.includes("?")) {
		return url;
	}

	let parsed: URL;

	try {
		parsed = new URL(url, "https://placeholder.invalid");
	} catch {
		return url;
	}

	const credentialKeys = [...parsed.searchParams.keys()].filter((key) =>
		CREDENTIAL_PARAM_RE.test(key)
	);

	if (credentialKeys.length === 0) {
		return url;
	}

	for (const key of credentialKeys) {
		parsed.searchParams.set(key, REDACTED);
	}

	const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(url);

	return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

const BREADCRUMB_URL_KEYS = ["url", "from", "to"] as const;

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
	if (!breadcrumb.data) {
		return breadcrumb;
	}

	const data = { ...breadcrumb.data };

	for (const key of BREADCRUMB_URL_KEYS) {
		if (typeof data[key] === "string") {
			data[key] = scrubUrl(data[key]);
		}
	}

	return { ...breadcrumb, data };
}

/**
 * An event without the one-time tokens our public links carry (PREC-4385). The browser SDK reports
 * the page URL and Referer on every error, and a navigation or fetch breadcrumb for every step
 * before it, so a crash on /change-password?token=… would have stored a token that sets that
 * user's password. An event with neither a request nor breadcrumbs is returned as it came.
 */
export function scrubBrowserEvent(event: ErrorEvent): ErrorEvent {
	if (!event.request && !event.breadcrumbs) {
		return event;
	}

	const request = event.request && {
		...event.request,
		url: scrubUrl(event.request.url),
		...(event.request.headers && {
			headers: Object.fromEntries(
				Object.entries(event.request.headers).map(([name, value]) => [
					name,
					/^referer$/i.test(name) ? (scrubUrl(value) ?? value) : value,
				])
			),
		}),
	};

	return {
		...event,
		...(request && { request }),
		...(event.breadcrumbs && { breadcrumbs: event.breadcrumbs.map(scrubBreadcrumb) }),
	};
}
